import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import {
  BadgeCheck, Music, ListMusic, Heart, Mic, Edit3, Save, X as XIcon,
  UserCircle, Play, Pause, Crown, DollarSign, TrendingUp, Landmark, ExternalLink,
  CheckCircle2, Loader2, AlertTriangle, Clock, ArrowDownToLine, Camera,
} from "lucide-react";
import { api, mediumArt, formatTime } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { usePlayer } from "@/contexts/PlayerContext";
import { toast } from "sonner";

/**
 * ProfileAvatarUploader — Profile-page-sized variant of the avatar
 * uploader. Larger hit area (matches the 128/176px hero avatar) and
 * a more pronounced hover overlay since the profile picture is a
 * focal element here. POSTs to ``/api/auto-artwork/upload-picture``
 * and updates BOTH the AuthContext user AND the page-local ``data``
 * so the new picture reflects in the hero immediately.
 */
const ProfileAvatarUploader = ({ user, setUser, data, setData, displayName }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onPick = () => inputRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      toast.error("Please pick a JPG, PNG or WEBP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be ≤ 5 MB.");
      return;
    }
    const fd = new FormData();
    fd.append("picture", file);
    setUploading(true);
    try {
      const r = await api.post("/artwork/upload-picture", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const newPic = r.data?.picture;
      if (newPic) {
        const cacheBusted = newPic + (newPic.includes("?") ? "&" : "?") + "t=" + Date.now();
        setUser({ ...user, picture: cacheBusted });
        setData({ ...data, picture: cacheBusted });
        toast.success("Profile picture updated.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onPick}
      disabled={uploading}
      data-testid="profile-avatar-upload-btn"
      className="group relative w-32 h-32 sm:w-44 sm:h-44 rounded-full overflow-hidden shadow-2xl border-4 border-white/10 focus:outline-none focus:ring-2 focus:ring-vermilion"
      title="Tap to change your profile picture"
    >
      {data.picture ? (
        <img
          src={data.picture}
          alt=""
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center">
          <span className="font-display text-5xl font-bold text-white">
            {(displayName[0] || "?").toUpperCase()}
          </span>
        </div>
      )}
      <div className={`absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 backdrop-blur-sm transition-opacity ${uploading ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
        {uploading ? (
          <Loader2 size={28} className="animate-spin text-white" />
        ) : (
          <>
            <Camera size={28} className="text-white" strokeWidth={2.2} />
            <span className="text-[10px] uppercase tracking-[0.18em] font-black text-white">
              Change photo
            </span>
          </>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onChange}
        className="hidden"
        data-testid="profile-avatar-upload-input"
      />
    </button>
  );
};

const StatPill = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/8">
    <Icon size={14} className="text-white/55" />
    <span className="text-xs text-white/55">{label}</span>
    <span className="text-xs font-semibold">{value}</span>
  </div>
);

// ProfilePayoutHistory — combines completed payouts + queued requests
// into a single timeline directly under the earnings card. Artists no
// longer need to dig into Settings to see when they last got paid.
const ProfilePayoutHistory = () => {
  const [done, setDone] = useState([]);
  const [pending, setPending] = useState([]);
  const [pendingTotal, setPendingTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    Promise.all([
      api.get("/connect/payouts", { params: { limit: 12 } }).catch(() => ({ data: { results: [] } })),
      api.get("/connect/my-pending").catch(() => ({ data: { queued: [], total_queued_usd: 0 } })),
    ]).then(([d, p]) => {
      if (cancel) return;
      setDone(d.data?.results || []);
      setPending(p.data?.queued || []);
      setPendingTotal(p.data?.total_queued_usd || 0);
    }).finally(() => !cancel && setLoading(false));
    return () => { cancel = true; };
  }, []);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
    } catch { return iso; }
  };
  const destLabel = (p) => {
    if (p.destination_type === "manual" || p.method === "manual" || p.method === "venmo" || p.method === "zelle") {
      return p.method ? p.method.charAt(0).toUpperCase() + p.method.slice(1) : "Manual";
    }
    if (p.destination_brand && p.destination_last4) return `${p.destination_brand} •••• ${p.destination_last4}`;
    if (p.destination_last4) return `•••• ${p.destination_last4}`;
    return "Bank";
  };

  if (loading) return null;
  if (done.length === 0 && pending.length === 0) {
    return (
      <div className="mt-4" data-testid="profile-payout-history-empty">
        <h3 className="text-sm font-semibold text-white/70 mb-2 uppercase tracking-wider">
          Payout history
        </h3>
        <div className="rounded-2xl bg-white/[0.03] border border-white/8 px-4 py-6 text-center">
          <ArrowDownToLine size={24} className="mx-auto text-white/25 mb-2" />
          <p className="text-sm text-white/55">No payouts yet</p>
          <p className="text-xs text-white/40 mt-1">Earn $5+ in streams to make your first cashout.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4" data-testid="profile-payout-history">
      <h3 className="text-sm font-semibold text-white/70 mb-2 uppercase tracking-wider">
        Payout history
      </h3>
      <div className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden divide-y divide-white/5">
        {pendingTotal > 0 && (
          <div className="px-4 py-3 bg-amber-300/[0.04] border-b border-amber-300/15">
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-amber-300" />
              <p className="text-xs text-amber-200">
                <b className="text-amber-100">${pendingTotal.toFixed(2)} queued</b> — processing within 24–48 hrs
              </p>
            </div>
          </div>
        )}
        {pending.map((p) => (
          <div
            key={p.request_id}
            data-testid={`profile-payout-pending-${p.request_id}`}
            className="px-4 py-3 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-amber-300/10 text-amber-300 flex items-center justify-center shrink-0">
                <Clock size={15} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">${p.amount_usd?.toFixed(2)}</p>
                <p className="text-xs text-white/45 truncate">Queued · {formatDate(p.created_at)} · {destLabel(p)}</p>
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-amber-300 bg-amber-300/10 border border-amber-300/30 px-2 py-1 rounded-sm">
              Pending
            </span>
          </div>
        ))}
        {done.map((p, i) => (
          <div
            key={p.transfer_id || `${i}-${p.created_at}`}
            data-testid={`profile-payout-done-${p.transfer_id || i}`}
            className="px-4 py-3 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-xl bg-emerald-400/10 text-emerald-300 flex items-center justify-center shrink-0">
                <ArrowDownToLine size={15} />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">${(p.amount_usd ?? p.amount_usd_cents / 100).toFixed(2)}</p>
                <p className="text-xs text-white/45 truncate">{formatDate(p.created_at)} · {destLabel(p)}</p>
                {p.note && <p className="text-[10px] text-white/35 truncate mt-0.5">{p.note}</p>}
              </div>
            </div>
            <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-300 bg-emerald-300/10 border border-emerald-300/30 px-2 py-1 rounded-sm">
              {p.status === "failed" ? "Failed" : "Paid"}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

const Profile = () => {
  const { userId } = useParams();
  const { user, setUser, login } = useAuth();
  const { current, playing, togglePlay, playTracks } = usePlayer();
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", artist_display_name: "", artist_bio: "" });
  const [saving, setSaving] = useState(false);
  const [connect, setConnect] = useState(null);     // /connect/status response
  const [connectBusy, setConnectBusy] = useState(false);

  const targetId = userId || (user?.user_id);
  const isSelf = !!user && (!userId || userId === user.user_id);

  const load = async () => {
    if (!targetId) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const url = isSelf ? "/profile/me" : `/profile/${targetId}`;
      const r = await api.get(url);
      setData(r.data);
      setForm({
        name: r.data.name || "",
        artist_display_name: r.data.artist_display_name || "",
        artist_bio: r.data.artist_bio || "",
      });
    } catch (err) {
      setData(null);
      toast.error("Profile not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [targetId, isSelf]);

  // Earnings: only fetch for own artist profile
  const [earnings, setEarnings] = useState(null);
  const refreshEarnings = async () => {
    if (!isSelf || !data?.is_artist) return;
    try {
      const [e, c] = await Promise.all([
        api.get("/profile/me/earnings"),
        api.get("/connect/status").catch(() => ({ data: null })),
      ]);
      setEarnings(e.data);
      setConnect(c.data);
    } catch {
      setEarnings(null);
      setConnect(null);
    }
  };
  useEffect(() => {
    if (!isSelf || !data?.is_artist) {
      setEarnings(null);
      setConnect(null);
      return;
    }
    refreshEarnings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelf, data?.is_artist]);

  // When Stripe redirects back after onboarding (?return=1 / ?refresh=1),
  // pull fresh status from Stripe and clear the URL params.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!isSelf || !data?.is_artist) return;
    if (params.get("return") || params.get("refresh")) {
      (async () => {
        try {
          await api.post("/connect/refresh");
          await refreshEarnings();
          if (params.get("return")) {
            toast.success("Connected to Stripe", {
              description: "We'll enable cashouts as soon as Stripe finishes verifying you.",
            });
          }
        } catch (e) {
          // silent — they may still be onboarding
        }
        navigate(location.pathname, { replace: true });
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, isSelf, data?.is_artist]);

  const startConnectOnboarding = async () => {
    setConnectBusy(true);
    try {
      const r = await api.post("/connect/onboard", { origin_url: window.location.origin });
      if (r.data?.url) {
        // Send the user to Stripe-hosted KYC
        window.location.href = r.data.url;
        return;
      }
      toast.error("Couldn't start onboarding");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (status === 503 || detail.toLowerCase().includes("invalid api key") || detail.toLowerCase().includes("real stripe")) {
        toast.error("Stripe Connect not configured", {
          description: "The platform admin needs to plug a real Stripe test key into the backend to enable real bank payouts. Your balance is safe and accruing.",
        });
      } else {
        toast.error(detail || "Stripe onboarding failed");
      }
    } finally {
      setConnectBusy(false);
    }
  };

  const cashOut = async () => {
    setConnectBusy(true);
    try {
      const r = await api.post("/connect/cashout", {});
      if (r.data?.queued) {
        // Platform Stripe balance was empty — request was queued and will
        // auto-process when funds arrive. Positive UX, no scary message.
        toast.success("Cashout requested!", {
          description: `$${r.data.amount_usd.toFixed(2)} reserved · arrives in your bank within 24–48 hours.`,
          duration: 8000,
        });
      } else {
        toast.success(`Sent $${r.data.amount_usd.toFixed(2)} to your bank`, {
          description: `Transfer ${r.data.transfer_id} · Stripe will deposit within 24 hours.`,
        });
      }
      await refreshEarnings();
    } catch (e) {
      toast.error("Cashout failed", {
        description: e?.response?.data?.detail || "Please try again",
        duration: 6000,
      });
    } finally {
      setConnectBusy(false);
    }
  };

  // Claimed artist catalog (iTunes top songs + albums) — fetched when a user
  // has claimed their artist page so they see all of THEIR music here, not
  // just the MP3s they uploaded directly.
  const [catalog, setCatalog] = useState(null);
  useEffect(() => {
    const aid = data?.artist_itunes_id;
    if (!aid) { setCatalog(null); return; }
    let cancel = false;
    api.get(`/artist/${aid}`)
      .then((r) => { if (!cancel) setCatalog(r.data); })
      .catch(() => { if (!cancel) setCatalog(null); });
    return () => { cancel = true; };
  }, [data?.artist_itunes_id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/profile/me", form);
      toast.success("Profile updated");
      setEditing(false);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!user && !userId) {
    return (
      <div className="px-4 sm:px-8 py-16 max-w-2xl mx-auto text-center">
        <UserCircle size={48} className="mx-auto text-white/30 mb-4" />
        <h1 className="font-display text-3xl font-semibold mb-2">Sign in to see your profile</h1>
        <p className="text-white/60 mb-6">Your library, playlists and uploads in one place.</p>
        <button
          data-testid="profile-signin-btn"
          onClick={login}
          className="bg-white text-black font-semibold rounded-full px-6 py-2.5 hover:scale-[1.03] transition"
        >
          Sign in
        </button>
      </div>
    );
  }

  if (loading) return <div className="px-4 sm:px-8 py-12 text-white/50">Loading…</div>;
  if (!data) return <div className="px-4 sm:px-8 py-12 text-white/50">Profile not found.</div>;

  const displayName = data.artist_display_name || data.name || "Listener";
  const isArtist = !!data.is_artist;
  const isVerified = !!data.verified || !!data.artist_verified_at;

  return (
    <div data-testid="profile-page" className="pb-12">
      {/* Hero header (responsive — stacks on mobile, side-by-side on desktop) */}
      <div className="relative px-4 sm:px-8 pt-8 pb-6">
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          style={{
            background: isArtist
              ? "linear-gradient(180deg, rgba(255,51,102,0.18) 0%, transparent 70%)"
              : "linear-gradient(180deg, rgba(157,76,221,0.15) 0%, transparent 70%)",
          }}
        />
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row gap-6 sm:items-end">
          <div className="shrink-0 mx-auto sm:mx-0">
            {isSelf ? (
              // Own profile → wrap in the uploader so users can tap to
              // replace their picture. Uses the same widget Settings does.
              <ProfileAvatarUploader user={user} setUser={setUser} data={data} setData={setData} displayName={displayName} />
            ) : data.picture ? (
              <img
                src={data.picture}
                alt=""
                className="w-32 h-32 sm:w-44 sm:h-44 rounded-full object-cover shadow-2xl border-4 border-white/10"
              />
            ) : (
              <div className="w-32 h-32 sm:w-44 sm:h-44 rounded-full bg-gradient-to-br from-[#FF2E00] via-[#FF2E00] to-[#E2FF31] flex items-center justify-center shadow-2xl">
                <span className="font-display text-5xl font-bold text-white">
                  {(displayName[0] || "?").toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 text-center sm:text-left">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/45 font-bold mb-1">
              {isArtist ? "Artist" : "Listener"}
            </p>
            {!editing ? (
              <h1
                data-testid="profile-display-name"
                className="font-display text-4xl sm:text-6xl font-bold tracking-tight flex items-center gap-2 justify-center sm:justify-start"
              >
                <span className="truncate">{displayName}</span>
                {isVerified && (
                  <BadgeCheck
                    size={28}
                    className="text-[#E2FF31] shrink-0"
                    fill="currentColor"
                    stroke="black"
                    data-testid="profile-verified-badge"
                  />
                )}
              </h1>
            ) : (
              <input
                data-testid="profile-edit-display-name"
                value={isArtist ? form.artist_display_name : form.name}
                onChange={(e) =>
                  setForm((f) =>
                    isArtist ? { ...f, artist_display_name: e.target.value } : { ...f, name: e.target.value }
                  )
                }
                className="font-display text-3xl sm:text-4xl font-bold bg-white/[0.05] border border-white/15 rounded-lg px-3 py-2 w-full sm:max-w-md"
              />
            )}

            {/* Bio */}
            {!editing ? (
              data.artist_bio && (
                <p className="text-sm sm:text-base text-white/65 mt-2 max-w-xl leading-relaxed">
                  {data.artist_bio}
                </p>
              )
            ) : isArtist ? (
              <textarea
                data-testid="profile-edit-bio"
                value={form.artist_bio}
                onChange={(e) => setForm((f) => ({ ...f, artist_bio: e.target.value.slice(0, 600) }))}
                rows={2}
                placeholder="Your bio (≤600 chars)"
                className="w-full sm:max-w-xl mt-2 bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30 resize-none"
              />
            ) : null}

            {/* Stats */}
            <div className="flex flex-wrap gap-2 mt-4 justify-center sm:justify-start">
              {isArtist && (
                <StatPill icon={Music} label="Tracks" value={data.stats?.uploads ?? 0} />
              )}
              <StatPill
                icon={ListMusic}
                label="Playlists"
                value={data.stats?.public_playlists ?? data.stats?.playlists ?? 0}
              />
              {isSelf && data.stats?.liked_songs !== undefined && (
                <StatPill icon={Heart} label="Liked" value={data.stats.liked_songs} />
              )}
              {isSelf && data.premium_tier && data.premium_tier !== "free" && (
                <StatPill
                  icon={Crown}
                  label="Plan"
                  value={data.premium_tier === "artist" ? "Artist" : data.premium_tier === "premium" ? "Listener" : "Trial"}
                />
              )}
            </div>

            {/* Edit toolbar */}
            {isSelf && (
              <div className="mt-5 flex gap-2 justify-center sm:justify-start">
                {!editing ? (
                  <button
                    data-testid="profile-edit-btn"
                    onClick={() => setEditing(true)}
                    className="flex items-center gap-1.5 bg-white/8 hover:bg-white/15 text-sm font-semibold rounded-full px-4 py-2 transition"
                  >
                    <Edit3 size={14} /> Edit profile
                  </button>
                ) : (
                  <>
                    <button
                      data-testid="profile-save-btn"
                      onClick={save}
                      disabled={saving}
                      className="flex items-center gap-1.5 bg-[#FF2E00] hover:bg-[#E62900] text-white text-sm font-semibold rounded-full px-4 py-2 transition disabled:opacity-50"
                    >
                      <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button
                      data-testid="profile-cancel-btn"
                      onClick={() => { setEditing(false); load(); }}
                      className="flex items-center gap-1.5 bg-white/8 hover:bg-white/15 text-sm rounded-full px-4 py-2 transition"
                    >
                      <XIcon size={14} /> Cancel
                    </button>
                  </>
                )}
                {!isArtist && (
                  <Link
                    to="/upload"
                    data-testid="profile-become-artist-btn"
                    className="flex items-center gap-1.5 bg-gradient-to-r from-[#FF2E00] to-[#FF2E00] text-white text-sm font-semibold rounded-full px-4 py-2 hover:opacity-90 transition"
                  >
                    <Mic size={14} /> Become an artist
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Earnings (artist's own profile only) */}
      {isSelf && data.is_artist && earnings && (
        <section
          data-testid="profile-earnings"
          className="px-4 sm:px-8 max-w-5xl mx-auto mt-6 mb-8"
        >
          <div className="relative overflow-hidden rounded-3xl p-6 sm:p-8 border border-[#E2FF31]/25 bg-gradient-to-br from-[#E2FF31]/8 via-[#FF2E00]/8 to-[#FF2E00]/8">
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#E2FF31]/15 blur-3xl" />
            <div className="relative">
              <p className="text-[11px] uppercase tracking-[0.3em] text-[#E2FF31] font-bold mb-1.5 flex items-center gap-1.5">
                <TrendingUp size={12} /> Artist Earnings
              </p>
              <h2 className="font-display text-3xl font-semibold tracking-tight mb-1">
                ${earnings.total_earnings_usd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h2>
              <p className="text-sm text-white/55 mb-5">
                Total balance from {earnings.total_streams.toLocaleString()} stream{earnings.total_streams === 1 ? "" : "s"} at <b>${earnings.stream_rate_usd.toFixed(2)}</b>/stream.
              </p>

              {/* Split: current uploads vs claimed iTunes catalog */}
              {(earnings.catalog_streams > 0 || earnings.claimed_artists_count > 0) && (
                <div
                  data-testid="earnings-split"
                  className="grid grid-cols-2 gap-3 mb-5"
                >
                  <div className="rounded-xl p-3 bg-white/[0.04] border border-white/8">
                    <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Your uploads</p>
                    <p className="font-display text-xl font-semibold mt-0.5">
                      ${(earnings.upload_streams * earnings.stream_rate_usd).toFixed(2)}
                    </p>
                    <p className="text-[11px] text-white/45">{earnings.upload_streams.toLocaleString()} streams</p>
                  </div>
                  <div className="rounded-xl p-3 bg-gradient-to-br from-[#E2FF31]/10 to-transparent border border-[#E2FF31]/25">
                    <p className="text-[10px] uppercase tracking-wider text-[#E2FF31] font-bold">iTunes catalog</p>
                    <p className="font-display text-xl font-semibold mt-0.5">
                      ${(earnings.catalog_streams * earnings.stream_rate_usd).toFixed(2)}
                    </p>
                    <p className="text-[11px] text-white/55">
                      {earnings.catalog_streams.toLocaleString()} streams
                      {earnings.claimed_artists_count > 0 && (
                        <> · {earnings.claimed_artists_count} claimed artist{earnings.claimed_artists_count === 1 ? "" : "s"}</>
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                <div className="rounded-xl p-3 bg-white/5 border border-white/8">
                  <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Last 30 days</p>
                  <p className="font-display text-xl font-semibold mt-0.5">
                    ${earnings.recent_earnings_30d_usd.toFixed(2)}
                  </p>
                  <p className="text-[11px] text-white/45">{earnings.recent_streams_30d} streams</p>
                </div>
                <div className="rounded-xl p-3 bg-white/5 border border-white/8">
                  <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Tracks</p>
                  <p className="font-display text-xl font-semibold mt-0.5">{earnings.uploads_count}</p>
                  <p className="text-[11px] text-white/45">Published</p>
                </div>
                <div className="rounded-xl p-3 bg-white/5 border border-white/8 col-span-2 sm:col-span-1">
                  <p className="text-[10px] uppercase tracking-wider text-white/45 font-bold">Rate</p>
                  <p className="font-display text-xl font-semibold mt-0.5 text-[#E2FF31]">${earnings.stream_rate_usd.toFixed(2)}</p>
                  <p className="text-[11px] text-white/45">per stream · 65× Spotify</p>
                </div>
              </div>

              {/* Payout status / cashout buttons */}
              {(() => {
                const status = connect?.status || "not_started";
                const available = connect?.balance?.available_usd ?? 0;
                const minPayout = connect?.min_payout_usd ?? 5;
                const canCashOut = !!connect?.can_cash_out;
                const reqs = connect?.requirements_due || [];

                if (status === "not_started") {
                  return (
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        data-testid="connect-setup-btn"
                        onClick={startConnectOnboarding}
                        disabled={connectBusy}
                        className="bg-[#E2FF31] hover:bg-[#d4f01a] text-black font-semibold rounded-full px-5 py-2.5 transition flex items-center gap-2 disabled:opacity-60"
                      >
                        {connectBusy ? <Loader2 size={16} className="animate-spin" /> : <Landmark size={16} />}
                        Set up payouts
                      </button>
                      <span className="text-xs text-white/45 flex items-center gap-1.5">
                        <ExternalLink size={11} /> Secure KYC via Stripe · 2 min setup
                      </span>
                    </div>
                  );
                }
                if (status === "pending") {
                  return (
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        data-testid="connect-continue-btn"
                        onClick={startConnectOnboarding}
                        disabled={connectBusy}
                        className="bg-white text-black font-semibold rounded-full px-5 py-2.5 transition flex items-center gap-2 disabled:opacity-60"
                      >
                        {connectBusy ? <Loader2 size={16} className="animate-spin" /> : <Landmark size={16} />}
                        Finish payout setup
                      </button>
                      <span className="text-xs text-white/55 flex items-center gap-1.5">
                        <AlertTriangle size={11} className="text-amber-400" /> Stripe still needs a few details before you can cash out
                        {reqs.length > 0 && <span className="text-white/40">· {reqs.length} field{reqs.length === 1 ? "" : "s"} left</span>}
                      </span>
                    </div>
                  );
                }
                if (status === "restricted") {
                  return (
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        data-testid="connect-fix-btn"
                        onClick={startConnectOnboarding}
                        disabled={connectBusy}
                        className="bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-full px-5 py-2.5 transition flex items-center gap-2 disabled:opacity-60"
                      >
                        {connectBusy ? <Loader2 size={16} className="animate-spin" /> : <AlertTriangle size={16} />}
                        Fix payout issue
                      </button>
                      <span className="text-xs text-white/55">Stripe has paused your payouts — re-open onboarding to resolve.</span>
                    </div>
                  );
                }
                // verified
                return (
                  <div className="flex items-center gap-3 flex-wrap">
                    <button
                      data-testid="cash-out-btn"
                      onClick={cashOut}
                      disabled={!canCashOut || connectBusy}
                      className="bg-[#E2FF31] hover:bg-[#d4f01a] disabled:opacity-50 disabled:cursor-not-allowed text-black font-semibold rounded-full px-5 py-2.5 transition flex items-center gap-2"
                    >
                      {connectBusy ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
                      {canCashOut ? `Cash out $${available.toFixed(2)}` : `Cash out (min $${minPayout.toFixed(2)})`}
                    </button>
                    <span className="text-xs text-white/55 flex items-center gap-1.5">
                      <CheckCircle2 size={11} className="text-emerald-400" /> Bank verified · payouts in 24 hours
                    </span>
                  </div>
                );
              })()}

              {/* Available / paid-out breakdown */}
              {connect?.balance && (
                <div className="mt-4 flex flex-wrap gap-3 text-xs text-white/55">
                  <span><b className="text-white">${connect.balance.available_usd.toFixed(2)}</b> available</span>
                  {connect.balance.paid_out_usd > 0 && (
                    <span>· <b className="text-white">${connect.balance.paid_out_usd.toFixed(2)}</b> paid out</span>
                  )}
                  {connect.balance.pending_usd > 0 && (
                    <span>· <b className="text-amber-300">${connect.balance.pending_usd.toFixed(2)}</b> in transit</span>
                  )}
                </div>
              )}

              {/* Revenue split breakdown — 90% artist / 10% platform.
                  Only shown when the artist actually has earnings, so the
                  Profile page doesn't feel cluttered for new uploaders. */}
              {connect?.balance && (connect.balance.gross_usd || 0) > 0 && (
                <div
                  data-testid="revenue-split-breakdown"
                  className="mt-4 p-3 bg-gradient-to-br from-[#B580FF]/10 to-[#5E8BFF]/5 border border-white/10 rounded-xl"
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-white/55 font-semibold mb-2">
                    <span>Revenue split · 90% you / 10% platform</span>
                    <span className="text-emerald-300">${(connect.balance.stream_rate_usd_net ?? 0.18).toFixed(2)}/stream net</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-white/40 text-[10px]">Gross</div>
                      <div className="font-display text-base text-white tabular-nums">
                        ${connect.balance.gross_usd.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-white/40 text-[10px]">Platform fee (10%)</div>
                      <div className="font-display text-base text-white/55 tabular-nums">
                        −${connect.balance.platform_fee_usd.toFixed(2)}
                      </div>
                    </div>
                    <div>
                      <div className="text-emerald-300/70 text-[10px]">Your net (90%)</div>
                      <div className="font-display text-base text-emerald-300 tabular-nums font-semibold">
                        ${connect.balance.net_usd.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Monthly cap progress — only shown when artist has hit it or is close */}
              {connect?.balance && connect.balance.monthly_cap_usd > 0 && (() => {
                const cap = connect.balance.monthly_cap_usd;
                const paid = connect.balance.paid_this_month_usd || 0;
                const pct = Math.min(100, Math.round((paid / cap) * 100));
                const isCapped = (connect.balance.uncapped_available_usd || 0) > (connect.balance.available_usd || 0);
                if (!isCapped && pct < 50) return null;  // hide for low-volume artists
                return (
                  <div
                    data-testid="monthly-cap-progress"
                    className="mt-4 p-3 bg-white/[0.03] border border-white/10 rounded-xl"
                  >
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="text-white/60">Monthly cashout limit</span>
                      <span className="text-white">
                        ${paid.toFixed(2)} / ${cap.toFixed(2)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${
                          pct >= 100 ? "bg-amber-400" : "bg-gradient-to-r from-[#B580FF] to-[#5E8BFF]"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {isCapped && (
                      <p className="text-[11px] text-amber-300 mt-2">
                        You&apos;ve reached this month&apos;s cashout cap. Remaining earnings roll over
                        and become cashable on the 1st of next month.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Top earning tracks */}
          {earnings.top_tracks && earnings.top_tracks.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-semibold text-white/70 mb-2 uppercase tracking-wider">
                Top earning tracks
              </h3>
              <div className="rounded-2xl bg-white/[0.03] border border-white/8 divide-y divide-white/5">
                {earnings.top_tracks.map((t, i) => (
                  <div
                    key={t.track_id}
                    data-testid={`earnings-row-${t.track_id}`}
                    className="grid grid-cols-[24px_40px_1fr_auto_auto] items-center gap-3 px-4 py-2.5"
                  >
                    <span className="text-xs text-white/40 font-mono tabular-nums">{i + 1}</span>
                    {t.cover_url ? (
                      <img src={t.cover_url} alt="" className="w-10 h-10 rounded object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/40" />
                    )}
                    <p className="text-sm font-medium truncate">{t.track_name}</p>
                    <span className="text-xs text-white/45 hidden sm:inline">{t.plays} plays</span>
                    <span className="text-sm font-semibold text-[#E2FF31] tabular-nums">
                      ${t.earnings_usd.toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payout history — completed transfers + queued requests */}
          <ProfilePayoutHistory />
        </section>
      )}

      {/* Claimed Artist Catalog — iTunes top songs + albums for the
          artist page this user verified. Shown to ANY visitor of the
          profile (it's their public catalog). */}
      {catalog?.topSongs?.length > 0 && (
        <section className="px-4 sm:px-8 max-w-5xl mx-auto mt-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-display text-2xl font-semibold flex items-center gap-2">
              <Music size={20} /> Top songs
            </h2>
            <Link
              to={`/artist/${data.artist_itunes_id}`}
              className="text-xs text-white/55 hover:text-white"
              data-testid="profile-view-full-catalog"
            >
              View full catalog →
            </Link>
          </div>
          <div
            data-testid="profile-catalog-songs"
            className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden"
          >
            {catalog.topSongs.slice(0, 10).map((t, i) => {
              const isCur = current?.trackId === t.trackId;
              return (
                <div
                  key={t.trackId || i}
                  data-testid={`profile-catalog-track-${t.trackId}`}
                  className="grid grid-cols-[40px_60px_1fr_auto] items-center gap-3 px-4 py-2.5 hover:bg-white/5 border-b border-white/5 last:border-b-0 transition"
                >
                  <button
                    onClick={() => {
                      if (isCur) togglePlay();
                      else playTracks(catalog.topSongs, i);
                    }}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
                    aria-label={isCur && playing ? "Pause" : "Play"}
                  >
                    {isCur && playing
                      ? <Pause size={14} fill="currentColor" />
                      : <Play size={14} fill="currentColor" className="ml-0.5" />}
                  </button>
                  {t.artworkUrl100 ? (
                    <img src={t.artworkUrl100} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/40 flex items-center justify-center">
                      <Music size={16} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isCur ? "text-[#FF2E00]" : "text-white"}`}>
                      {t.trackName}
                    </p>
                    <p className="text-xs text-white/50 truncate">{t.collectionName}</p>
                  </div>
                  <span className="text-xs text-white/40 hidden sm:inline">
                    {t.trackTimeMillis ? formatTime(t.trackTimeMillis) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Albums from the claimed catalog */}
      {catalog?.albums?.length > 0 && (
        <section className="px-4 sm:px-8 max-w-5xl mx-auto mt-8">
          <h2 className="font-display text-2xl font-semibold mb-3 flex items-center gap-2">
            <ListMusic size={20} /> Albums
          </h2>
          <div
            data-testid="profile-catalog-albums"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4"
          >
            {catalog.albums.slice(0, 10).map((a) => (
              <Link
                key={a.collectionId}
                to={`/album/${a.collectionId}`}
                data-testid={`profile-album-${a.collectionId}`}
                className="group"
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-white/5">
                  {a.artworkUrl100 ? (
                    <img
                      src={(a.artworkUrl100 || "").replace("100x100bb", "400x400bb")}
                      alt={a.collectionName}
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music size={28} className="text-white/40" />
                    </div>
                  )}
                </div>
                <p className="text-sm font-medium truncate mt-2 group-hover:text-[#FF2E00] transition-colors">
                  {a.collectionName}
                </p>
                <p className="text-[11px] text-white/45 truncate">
                  {a.releaseDate ? new Date(a.releaseDate).getFullYear() : ""}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Tracks (artists only) */}
      {data.uploads && data.uploads.length > 0 && (
        <section className="px-4 sm:px-8 max-w-5xl mx-auto mt-8">
          <h2 className="font-display text-2xl font-semibold mb-3 flex items-center gap-2">
            <Music size={20} /> {catalog?.topSongs?.length ? "Your uploads" : "Tracks"}
          </h2>
          <div
            data-testid="profile-tracks"
            className="rounded-2xl bg-white/[0.03] border border-white/8 overflow-hidden"
          >
            {data.uploads.map((t, i) => {
              const isCur = current?.trackId === t.trackId;
              return (
                <div
                  key={t.trackId}
                  data-testid={`profile-track-${t.trackId}`}
                  className="grid grid-cols-[40px_60px_1fr_auto] items-center gap-3 px-4 py-2.5 hover:bg-white/5 border-b border-white/5 last:border-b-0 transition"
                >
                  <button
                    onClick={() => {
                      if (isCur) togglePlay();
                      else playTracks(data.uploads, i);
                    }}
                    className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
                  >
                    {isCur && playing
                      ? <Pause size={14} fill="currentColor" />
                      : <Play size={14} fill="currentColor" className="ml-0.5" />}
                  </button>
                  {t.artworkUrl100 ? (
                    <img src={t.artworkUrl100} alt="" className="w-10 h-10 rounded object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gradient-to-br from-[#FF2E00]/40 to-[#FF2E00]/40 flex items-center justify-center">
                      <Music size={16} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isCur ? "text-[#FF2E00]" : "text-white"}`}>
                      {t.trackName}
                    </p>
                    <p className="text-xs text-white/50 truncate">{t.collectionName}</p>
                  </div>
                  <span className="text-xs text-white/40 hidden sm:inline">
                    {t.trackTimeMillis ? formatTime(t.trackTimeMillis) : "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Playlists */}
      {data.playlists && data.playlists.length > 0 && (
        <section className="px-4 sm:px-8 max-w-5xl mx-auto mt-8">
          <h2 className="font-display text-2xl font-semibold mb-3 flex items-center gap-2">
            <ListMusic size={20} /> Playlists
          </h2>
          <div
            data-testid="profile-playlists"
            className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
          >
            {data.playlists.map((p) => (
              <Link
                to={`/playlist/${p.id}`}
                key={p.id}
                data-testid={`profile-playlist-${p.id}`}
                className="group rounded-xl bg-white/[0.03] border border-white/8 hover:border-white/20 transition p-3"
              >
                <div className="aspect-square mb-2 rounded-lg overflow-hidden bg-gradient-to-br from-[#FF2E00]/30 to-[#FF2E00]/20">
                  {p.cover ? (
                    <img
                      src={mediumArt(p.cover)}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ListMusic size={28} className="text-white/40" />
                    </div>
                  )}
                </div>
                <p className="font-medium text-sm truncate">{p.name}</p>
                <p className="text-xs text-white/45">
                  {p.track_count} song{p.track_count === 1 ? "" : "s"}
                  {!p.is_public && isSelf && " · Private"}
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Empty state */}
      {(!data.uploads || data.uploads.length === 0) &&
       (!data.playlists || data.playlists.length === 0) &&
       (!catalog?.topSongs || catalog.topSongs.length === 0) && (
        <div className="text-center py-16 px-4 max-w-md mx-auto">
          <UserCircle size={36} className="mx-auto text-white/30 mb-3" />
          <p className="text-sm text-white/55">
            {isSelf
              ? "Your profile is empty. Like a song or create a playlist to fill it up!"
              : `${displayName} hasn't shared any music yet.`}
          </p>
        </div>
      )}
    </div>
  );
};

export default Profile;
